import { useCallback } from "react";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { inviteErrorMessage } from "../helpers/inviteErrorMessage";
import { inviteKindFromTask } from "../helpers/inviteTaskHelpers";
import {
  acceptTeleconsultoriaInvite,
  rejectTeleconsultoriaInvite,
} from "../services/respondTeleconsultoriaInvite";

/**
 * Ações do CONVIDADO sobre a Task de convite.
 * - accept: inclui o convidado no Appointment, marca a Task e abre o atendimento
 *   do teleconsultor (mesmo `verifyEncounter` do botão "Iniciar teleconsultoria").
 * - reject: só marca a Task como recusada.
 * A Task é atualizada no contexto na hora, para a central refletir sem esperar o SSE.
 */
export function useTeleconsultoriaInviteActions() {
  const { user } = useSessionContext();
  const { verifyEncounter } = useClinicalRegisterContext();
  const { updateTaskInContext } = useTaskWatchingContext();
  const practitionerReference = `Practitioner/${oids.cpf}-${user?.username}`;

  const acceptTask = useAsyncTask({
    task: useCallback(
      (task: fhir5.Task) =>
        acceptTeleconsultoriaInvite({ task, practitionerReference }),
      [practitionerReference],
    ),
  });
  const rejectTask = useAsyncTask({ task: rejectTeleconsultoriaInvite });

  const { run: runAccept } = acceptTask;
  const accept = useCallback(
    async (task: fhir5.Task, onBeforeNavigate?: () => void) => {
      try {
        const appointment = await runAccept(task);
        if (task.id) updateTaskInContext(task.id, { status: "accepted" });
        // Reserva com horário: só aceita (entra no Appointment). O atendimento
        // começa depois, por "Minhas teleconsultorias marcadas" ou pelo dashboard.
        if (inviteKindFromTask(task) === "scheduled") {
          toast.success(
            "Teleconsultoria aceita. Ela aparece em Minhas teleconsultorias marcadas.",
          );
          return;
        }
        onBeforeNavigate?.();
        // verifyEncounter mostra os próprios avisos (ex.: origem ainda não iniciou).
        await verifyEncounter(appointment, false, { asTeleconsultor: true });
      } catch (error) {
        console.error("useTeleconsultoriaInviteActions.accept", error);
        toast.error(inviteErrorMessage(error, "Não foi possível aceitar o convite."));
      }
    },
    [runAccept, updateTaskInContext, verifyEncounter],
  );

  const { run: runReject } = rejectTask;
  const reject = useCallback(
    async (task: fhir5.Task) => {
      try {
        await runReject(task);
        if (task.id) updateTaskInContext(task.id, { status: "rejected" });
        toast.info("Convite recusado.");
      } catch (error) {
        console.error("useTeleconsultoriaInviteActions.reject", error);
        toast.error(inviteErrorMessage(error, "Não foi possível recusar o convite."));
      }
    },
    [runReject, updateTaskInContext],
  );

  return {
    accept,
    reject,
    isAccepting: acceptTask.loading,
    isRejecting: rejectTask.loading,
  };
}
